import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';
import { Icon } from './icons';
import { Badge, Button, StatusBadge } from './ui';
import { asRecord, field } from '../api';
import { formatDate } from '../utils';

interface EvidenceState {
  title: string;
  eyebrow?: string;
  content: unknown;
}

interface EvidenceContextValue {
  evidence: EvidenceState | null;
  openEvidence: (value: EvidenceState) => void;
  closeEvidence: () => void;
}

const EvidenceContext = createContext<EvidenceContextValue | null>(null);

export function EvidenceProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [evidence, setEvidence] = useState<EvidenceState | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const openEvidence = useCallback((value: EvidenceState) => {
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEvidence(value);
  }, []);
  const closeEvidence = useCallback(() => {
    setEvidence(null);
    requestAnimationFrame(() => returnFocus.current?.focus());
  }, []);
  const value = useMemo(
    () => ({ evidence, openEvidence, closeEvidence }),
    [evidence, openEvidence, closeEvidence],
  );
  return (
    <EvidenceContext.Provider value={value}>
      {children}
      <EvidenceDrawer />
    </EvidenceContext.Provider>
  );
}

export function useEvidence(): EvidenceContextValue {
  const context = useContext(EvidenceContext);
  if (!context) throw new Error('useEvidence must be inside EvidenceProvider');
  return context;
}

function EvidenceDrawer(): React.JSX.Element | null {
  const { evidence, closeEvidence } = useEvidence();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!evidence) return;
    document.documentElement.dataset.evidenceOpen = 'true';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEvidence();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      delete document.documentElement.dataset.evidenceOpen;
      window.removeEventListener('keydown', onKey);
    };
  }, [evidence, closeEvidence]);
  if (!evidence) return null;
  const item = asRecord(evidence.content);
  const safeText = field<string>(item, 'safe_render', 'safeRender', 'summary', 'description');
  const sourceIds = field<string[]>(item, 'source_ids', 'sourceIds') ?? [];
  return (
    <aside
      className="evidence-drawer"
      role="dialog"
      aria-label="Contextual evidence"
      aria-live="polite"
    >
      <header className="evidence-drawer__header">
        <div>
          <p className="eyebrow">{evidence.eyebrow ?? 'Evidence context'}</p>
          <h2>{evidence.title}</h2>
        </div>
        <Button
          ref={closeRef}
          variant="quiet"
          className="icon-button"
          onClick={closeEvidence}
          aria-label="Close evidence drawer"
        >
          <Icon name="close" />
        </Button>
      </header>
      <div className="evidence-drawer__body">
        <div className="evidence-meta">
          <StatusBadge status={field<string>(item, 'status', 'review_status')} />
          {field<string>(item, 'jurisdiction') ? (
            <Badge>{field<string>(item, 'jurisdiction')}</Badge>
          ) : null}
        </div>
        {safeText ? (
          <p className="evidence-safe-render">{safeText}</p>
        ) : (
          <p className="muted">No approved evidence narrative was returned.</p>
        )}
        <dl className="definition-list">
          {field<string>(item, 'authority_level', 'authority_type') ? (
            <>
              <dt>Authority</dt>
              <dd>{field<string>(item, 'authority_level', 'authority_type')}</dd>
            </>
          ) : null}
          {field<string>(item, 'last_verified', 'retrieved_at') ? (
            <>
              <dt>Last verified</dt>
              <dd>{formatDate(field<string>(item, 'last_verified', 'retrieved_at'))}</dd>
            </>
          ) : null}
          {field<string>(item, 'next_review') ? (
            <>
              <dt>Next review</dt>
              <dd>{formatDate(field<string>(item, 'next_review'))}</dd>
            </>
          ) : null}
        </dl>
        {sourceIds.length ? (
          <section>
            <h3>Linked sources</h3>
            <div className="stack-list">
              {sourceIds.map((id) => (
                <Link
                  className="source-link"
                  to={`/sources?focus=${encodeURIComponent(id)}`}
                  key={id}
                >
                  <Icon name="sources" size={15} />
                  {id}
                  <Icon name="chevron" size={14} />
                </Link>
              ))}
            </div>
          </section>
        ) : null}
        {field<string[]>(item, 'forbidden_inferences')?.length ? (
          <section className="guardrail">
            <h3>
              <Icon name="shield" size={16} /> Interpretation guardrails
            </h3>
            <ul>
              {field<string[]>(item, 'forbidden_inferences')?.map((entry) => (
                <li key={entry}>{entry}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </aside>
  );
}

export function EvidenceButton({
  item,
  title,
  label = 'Evidence',
}: {
  item: unknown;
  title: string;
  label?: string;
}): React.JSX.Element {
  const { openEvidence } = useEvidence();
  return (
    <Button variant="quiet" icon="evidence" onClick={() => openEvidence({ title, content: item })}>
      {label}
    </Button>
  );
}
